import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { theme } from '../../theme';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const ServicesSection = styled.section`
  background: ${theme.colors.bgSecondary};
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 70% 40% at 50% 0%, rgba(${theme.colors.primaryRgb}, 0.08) 0%, transparent 70%);
  }
`;

export const Container = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.md}) {
    padding: 0 1rem;
  }
`;

export const SectionHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 430px);
  gap: 2rem;
  align-items: end;
  margin-bottom: 4rem;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2.5rem;
  }
`;

export const SectionTag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.85rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.primaryLight};
  background: rgba(${theme.colors.primaryRgb}, 0.1);
  border: 1px solid rgba(${theme.colors.primaryRgb}, 0.25);
  margin-bottom: 1rem;
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 700;
  letter-spacing: -0.06em;
  color: ${theme.colors.text};
  margin-bottom: 1rem;

  span {
    background: ${theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const SectionSubtitle = styled.p`
  font-size: 1rem;
  color: ${theme.colors.textMuted};
  max-width: 430px;
  margin: 0;
  line-height: 1.75;

  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 1.25rem;

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

export const ServiceCard = styled.div<{ $accentColor: string; $featured: boolean }>`
  grid-column: span ${({ $featured }) => ($featured ? 7 : 5)};
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: 26px;
  padding: ${({ $featured }) => ($featured ? '1.7rem' : '1.45rem')};
  transition: ${theme.transitions.default};
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.5s ease both;
  display: grid;
  gap: 1.2rem;
  min-height: ${({ $featured }) => ($featured ? '320px' : '280px')};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ $accentColor }) => $accentColor};
  }

  &::after {
    content: '';
    position: absolute;
    inset: auto -10% -25% auto;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: ${({ $accentColor }) => $accentColor};
    opacity: 0.08;
    filter: blur(42px);
  }

  &:hover {
    border-color: rgba(${theme.colors.primaryRgb}, 0.3);
    transform: translateY(-6px);
    box-shadow: ${theme.shadows.card}, 0 12px 40px rgba(0, 0, 0, 0.3);
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-column: span 1;
    min-height: auto;
  }
`;

export const CardTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

export const ServiceNumber = styled.span`
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
`;

export const IconBox = styled.div<{ $color: string }>`
  position: relative;
  z-index: 1;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $color }) => `${$color}18`};
  border: 1px solid ${({ $color }) => `${$color}30`};
  color: ${({ $color }) => $color};
  font-size: 1.4rem;
  transition: ${theme.transitions.default};

  ${ServiceCard}:hover & {
    transform: scale(1.1);
  }
`;

export const CardBody = styled.div<{ $featured: boolean }>`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 1.1rem;

  ${({ $featured }) =>
    $featured &&
    css`
      grid-template-columns: minmax(0, 1fr) minmax(240px, 0.9fr);
      align-items: start;

      @media (max-width: ${theme.breakpoints.md}) {
        grid-template-columns: 1fr;
      }
    `}
`;

export const CardTitle = styled.h3`
  font-size: clamp(1.25rem, 2vw, 1.7rem);
  font-weight: 700;
  letter-spacing: -0.04em;
  color: ${theme.colors.text};
  margin-bottom: 0.75rem;
`;

export const CardDesc = styled.p`
  font-size: 0.92rem;
  color: ${theme.colors.textMuted};
  line-height: 1.7;
`;

export const FeatureList = styled.ul<{ $featured: boolean }>`
  display: grid;
  grid-template-columns: ${({ $featured }) => ($featured ? 'repeat(2, minmax(0, 1fr))' : '1fr')};
  gap: 0.55rem;

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.7rem 0.8rem;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.08);
  background: rgba(9, 13, 20, 0.52);
  font-size: 0.82rem;
  color: ${theme.colors.textSecondary};
  line-height: 1.45;

  svg {
    color: ${theme.colors.primaryLight};
    flex-shrink: 0;
    margin-top: 0.15rem;
  }
`;

export const CardFooter = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;
`;

export const CardLink = styled(Link)<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: 'Space Grotesk', 'Manrope', sans-serif;
  font-size: 0.86rem;
  font-weight: 700;
  color: ${({ $color }) => $color};
  transition: ${theme.transitions.default};

  &:hover {
    gap: 0.7rem;
  }
`;
